import type { PaginationMeta } from '../../../types/pagination';
import type { ApiClient } from '../client';

export interface CanteenMealPlan {
  id: string;
  plan_name: string;
  billing_cycle: string;
  amount_cents: number;
  meals_per_cycle?: number | null;
  active: boolean;
  notes?: string | null;
}

export interface CanteenCard {
  id: string;
  student_id: string;
  card_number: string;
  card_type: string;
  balance_units: number;
  status: string;
  issued_on: string;
  expires_on?: string | null;
}

export interface CanteenBill {
  id: string;
  student_id: string;
  bill_month: string;
  total_units: number;
  total_amount_cents: number;
  paid_amount_cents: number;
  status: string;
  due_date?: string | null;
}

export function bindCanteen(client: ApiClient) {
  return {
    async listCanteenMealPlans() {
      return client.request<{ meal_plans: CanteenMealPlan[] } & PaginationMeta>(
        '/canteen/meal-plans?limit=200'
      );
    },

    async createCanteenMealPlan(body: {
      plan_name: string;
      billing_cycle: string;
      amount_cents: number;
      meals_per_cycle?: number | null;
      notes?: string | null;
    }) {
      return client.request<{ meal_plan_id: string }>('/canteen/meal-plans', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async listCanteenCards() {
      return client.request<{ cards: CanteenCard[] } & PaginationMeta>(
        '/canteen/cards?limit=200'
      );
    },

    async issueCanteenCard(body: {
      student_id: string;
      card_number: string;
      card_type: string;
      initial_units?: number | null;
      issued_on: string;
      expires_on?: string | null;
      notes?: string | null;
    }) {
      return client.request<{ card_id: string }>('/canteen/cards', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async topUpCanteenCard(body: { card_id: string; units: number; notes?: string | null }) {
      return client.request<{ topup_id: string; card_id: string; new_balance_units: number }>(
        '/canteen/cards/top-up',
        {
          method: 'POST',
          body: JSON.stringify(body),
        }
      );
    },

    async listCanteenBills() {
      return client.request<{ bills: CanteenBill[] } & PaginationMeta>(
        '/canteen/bills?limit=200'
      );
    },

    async createCanteenBill(body: {
      student_id: string;
      bill_month: string;
      due_date?: string | null;
      notes?: string | null;
    }) {
      return client.request<{
        bill_id: string;
        total_units: number;
        total_amount_cents: number;
      }>('/canteen/bills', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async recordCanteenBillPayment(body: {
      bill_id: string;
      amount_cents: number;
      paid_on: string;
      payment_method?: string | null;
      reference_no?: string | null;
      notes?: string | null;
    }) {
      return client.request<{
        payment_id: string;
        bill_id: string;
        status: string;
        paid_amount_cents: number;
      }>('/canteen/bills/pay', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
  };
}
